import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Sparkles, Zap, Layers, CheckCircle2, ArrowRight, Mail, Send, Loader2, User, Palette, Code2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import WowCXLogo from '@/components/WowCXLogo';

// Use current origin for API calls to work in both preview and production
const API = `${window.location.origin}/api`;

const LandingPage = () => {
  const navigate = useNavigate();
  const [contactForm, setContactForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    company: '',
    inquiry_type: 'general',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.first_name || !contactForm.email || !contactForm.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, contactForm);
      toast.success('Thank you! We\'ll get back to you within 24 hours.');
      setContactForm({
        first_name: '',
        last_name: '',
        email: '',
        company: '',
        inquiry_type: 'general',
        message: ''
      });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to submit. Please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  const features = [
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "AI-Powered Design",
      description: "Our AI understands your business and creates custom designs tailored to your industry and audience."
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Lightning Fast",
      description: "Get professional website designs in minutes, not weeks. Iterate and refine until it's perfect."
    },
    {
      icon: <Layers className="w-6 h-6" />,
      title: "WordPress Ready",
      description: "Designs are built with Elementor and WordPress, making them easy to deploy and maintain."
    }
  ];

  const steps = [
    {
      step: "01",
      title: "Share Your Vision",
      description: "Tell us about your business, upload content, and share inspiration from sites you love."
    },
    {
      step: "02",
      title: "Choose Your Theme",
      description: "Select from our curated WordPress themes that match your industry and style."
    },
    {
      step: "03",
      title: "AI Creates Magic",
      description: "Our AI generates complete page designs with your content, ready for review."
    },
    {
      step: "04",
      title: "Refine & Freeze",
      description: "Request revisions with natural language. When perfect, freeze it and we'll build your site."
    }
  ];

  const personas = [
    {
      icon: <User className="w-8 h-8" />,
      title: "Entrepreneurs & Business Owners",
      tagline: "Design your dream website in minutes",
      description: "You don't need to know WordPress or design tools. Our AI guides you through the entire process to create professional designs.",
      benefits: ["No technical skills required", "Professional results", "Save weeks of time", "Full control over your vision"],
      color: "indigo"
    },
    {
      icon: <Palette className="w-8 h-8" />,
      title: "Designers & Agencies",
      tagline: "Speed up client projects 10x",
      description: "Rapidly prototype website designs for clients. Export as HTML/CSS for any platform—WordPress or not.",
      benefits: ["Instant design variations", "Export to any format", "Impress clients faster", "Focus on creativity, not grunt work"],
      color: "violet"
    },
    {
      icon: <Code2 className="w-8 h-8" />,
      title: "Developers & Freelancers",
      tagline: "Now you can design too",
      description: "Close deals without depending on designers. Create professional designs and convert to any platform.",
      benefits: ["No design skills needed", "Win more clients", "Full-stack independence", "Convert to React, HTML, WordPress"],
      color: "emerald"
    }
  ];

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="px-6 md:px-12 lg:px-24 py-4 bg-white border-b border-zinc-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <WowCXLogo size="lg" />
          
          {/* Navigation Menu */}
          <nav className="hidden md:flex items-center gap-1">
            <button
              data-testid="nav-features"
              onClick={() => document.getElementById('features')?.scrollIntoView({behavior: 'smooth'})}
              className="px-4 py-2 text-sm font-medium text-zinc-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
            >
              Features
            </button>
            <button
              data-testid="nav-how-it-works"
              onClick={() => document.getElementById('how-it-works')?.scrollIntoView({behavior: 'smooth'})}
              className="px-4 py-2 text-sm font-medium text-zinc-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
            >
              How It Works
            </button>
            <button
              data-testid="nav-pricing"
              onClick={() => document.getElementById('pricing')?.scrollIntoView({behavior: 'smooth'})}
              className="px-4 py-2 text-sm font-medium text-zinc-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
            >
              Pricing
            </button>
            <button
              data-testid="nav-contact"
              onClick={() => document.getElementById('contact')?.scrollIntoView({behavior: 'smooth'})}
              className="px-4 py-2 text-sm font-medium text-zinc-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
            >
              Contact
            </button>
          </nav>

          <div className="flex items-center gap-3">
            <Button 
              data-testid="header-signin-btn"
              variant="ghost"
              onClick={() => {
                window.location.href = `${window.location.origin}/api/auth/google/login?redirect=/dashboard`;
              }}
              className="text-zinc-600 hover:text-zinc-900"
            >
              Sign In
            </Button>
            <Button 
              data-testid="header-login-btn"
              onClick={() => navigate('/get-started')}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              Get Started Free
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32 relative overflow-hidden">
        <div className="hero-glow absolute inset-0 -z-10" />
        
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <motion.div 
            className="md:col-span-7"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-block px-4 py-2 bg-indigo-100 rounded-full mb-6">
              <span className="text-sm font-medium text-indigo-700">AI-Powered Website Design</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-4" style={{fontFamily: 'Outfit'}}>
              Design Stunning Websites in <span className="text-gradient">Minutes</span>
            </h1>
            
            <p className="text-2xl md:text-3xl font-semibold text-zinc-700 mb-6" style={{fontFamily: 'Outfit'}}>
              No Coding Required
            </p>
            
            <p className="text-xl text-zinc-600 mb-8 leading-relaxed">
              Stop wasting weeks on website design. Our AI creates stunning, professional designs 
              tailored to your business—ready to go live in hours, not months.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Button 
                data-testid="hero-get-started-btn"
                size="lg" 
                className="btn-hover h-14 px-8 text-lg bg-indigo-600 hover:bg-indigo-700"
                onClick={() => navigate('/get-started?redirect=/briefing')}
              >
                Start Designing Free <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                data-testid="hero-learn-more-btn"
                size="lg" 
                variant="outline" 
                className="h-14 px-8 text-lg"
                onClick={() => document.getElementById('contact')?.scrollIntoView({behavior: 'smooth'})}
              >
                Contact Us
              </Button>
            </div>
            
            <div className="mt-8 flex items-center gap-6 text-sm text-zinc-500">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                <span>No credit card required</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                <span>Ready in 24 hours</span>
              </div>
            </div>
          </motion.div>
          
          <motion.div 
            className="md:col-span-5"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1753162658084-3902c8d0dec0?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" 
                alt="Designer working" 
                className="rounded-2xl shadow-2xl animate-float"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">AI Design Ready</p>
                    <p className="text-xs text-zinc-500">In 5 minutes</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="px-6 md:px-12 lg:px-24 py-24 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4" style={{fontFamily: 'Outfit'}}>
              Why Choose Wow!CX Design Studio
            </h2>
            <p className="text-xl text-zinc-600">Professional results without the professional price tag</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                data-testid={`feature-card-${index}`}
                className="card-hover p-8 bg-zinc-50 rounded-2xl border border-zinc-200"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="w-14 h-14 rounded-xl bg-indigo-100 flex items-center justify-center mb-4 text-indigo-600">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold mb-3" style={{fontFamily: 'Outfit'}}>{feature.title}</h3>
                <p className="text-zinc-600 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Is This For Section */}
      <section className="px-6 md:px-12 lg:px-24 py-24 bg-gradient-to-b from-zinc-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4" style={{fontFamily: 'Outfit'}}>
              Who Is This For?
            </h2>
            <p className="text-xl text-zinc-600">Built for everyone who needs professional website designs</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {personas.map((persona, index) => (
              <motion.div
                key={index}
                data-testid={`persona-card-${index}`}
                className={`relative overflow-hidden rounded-2xl border shadow-sm hover:shadow-xl transition-all duration-300 ${
                  persona.color === 'indigo' ? 'bg-indigo-50/70 border-indigo-200' :
                  persona.color === 'violet' ? 'bg-violet-50/70 border-violet-200' :
                  'bg-emerald-50/70 border-emerald-200'
                }`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                viewport={{ once: true }}
              >
                {/* Colored top bar */}
                <div className={`h-2 ${
                  persona.color === 'indigo' ? 'bg-gradient-to-r from-indigo-500 to-indigo-600' :
                  persona.color === 'violet' ? 'bg-gradient-to-r from-violet-500 to-purple-600' :
                  'bg-gradient-to-r from-emerald-500 to-teal-600'
                }`} />
                
                <div className="p-8">
                  {/* Icon */}
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 ${
                    persona.color === 'indigo' ? 'bg-indigo-100 text-indigo-600' :
                    persona.color === 'violet' ? 'bg-violet-100 text-violet-600' :
                    'bg-emerald-100 text-emerald-600'
                  }`}>
                    {persona.icon}
                  </div>
                  
                  {/* Title & Tagline */}
                  <h3 className="text-xl font-bold mb-2" style={{fontFamily: 'Outfit'}}>{persona.title}</h3>
                  <p className={`text-lg font-medium mb-4 ${
                    persona.color === 'indigo' ? 'text-indigo-600' :
                    persona.color === 'violet' ? 'text-violet-600' :
                    'text-emerald-600'
                  }`}>
                    &ldquo;{persona.tagline}&rdquo;
                  </p>
                  
                  {/* Description */}
                  <p className="text-zinc-600 mb-6 leading-relaxed">{persona.description}</p>
                  
                  {/* Benefits */}
                  <ul className="space-y-2">
                    {persona.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-zinc-700">
                        <CheckCircle2 className={`w-4 h-4 flex-shrink-0 ${
                          persona.color === 'indigo' ? 'text-indigo-500' :
                          persona.color === 'violet' ? 'text-violet-500' :
                          'text-emerald-500'
                        }`} />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
          
          {/* Bottom CTA */}
          <div className="text-center mt-12">
            <Button 
              data-testid="persona-cta-btn"
              size="lg" 
              className="btn-hover h-14 px-8 text-lg bg-indigo-600 hover:bg-indigo-700"
              onClick={() => navigate('/get-started?redirect=/briefing')}
            >
              Start Your Free Design <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="px-6 md:px-12 lg:px-24 py-24 scroll-mt-20">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4" style={{fontFamily: 'Outfit'}}>
              From Idea to Website in 4 Steps
            </h2>
            <p className="text-xl text-zinc-600">Our AI handles the heavy lifting</p>
          </div>
          
          <div className="space-y-8">
            {steps.map((item, index) => (
              <motion.div
                key={index}
                data-testid={`step-card-${index}`}
                className="flex gap-6 items-start"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="flex-shrink-0 w-16 h-16 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center">
                  <span className="text-white font-bold text-xl" style={{fontFamily: 'Outfit'}}>{item.step}</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold mb-2" style={{fontFamily: 'Outfit'}}>{item.title}</h3>
                  <p className="text-zinc-600 leading-relaxed">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section / Pricing */}
      <section id="pricing" className="px-6 md:px-12 lg:px-24 py-24 bg-gradient-to-br from-indigo-600 to-violet-600 scroll-mt-20">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-block px-4 py-2 bg-white/20 rounded-full mb-6">
            <span className="text-sm font-medium text-white">Simple, Affordable Pricing</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 text-white" style={{fontFamily: 'Outfit'}}>
            Start Free. Pay Only When You're Ready.
          </h2>
          <p className="text-xl text-indigo-100 mb-8">
            Sign up and get free credits to create your first design — no strings attached. Top up anytime as you grow.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <div className="flex items-center gap-2 text-indigo-100">
              <CheckCircle2 className="w-5 h-5 text-green-300" />
              <span>Free credits to start</span>
            </div>
            <div className="flex items-center gap-2 text-indigo-100">
              <CheckCircle2 className="w-5 h-5 text-green-300" />
              <span>No credit card required</span>
            </div>
            <div className="flex items-center gap-2 text-indigo-100">
              <CheckCircle2 className="w-5 h-5 text-green-300" />
              <span>Pay as you go</span>
            </div>
          </div>
          <Button 
            data-testid="cta-start-now-btn"
            size="lg" 
            className="btn-hover h-14 px-8 text-lg bg-white text-indigo-600 hover:bg-zinc-50"
            onClick={() => navigate('/get-started?redirect=/briefing')}
          >
            Start Your Project Free <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="px-6 md:px-12 lg:px-24 py-24 bg-white scroll-mt-20">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4" style={{fontFamily: 'Outfit'}}>
              Get in Touch
            </h2>
            <p className="text-xl text-zinc-600">Have questions? We&apos;d love to hear from you.</p>
          </div>
          
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <form onSubmit={handleContactSubmit} className="space-y-6" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">First Name *</label>
                  <Input
                    data-testid="contact-first-name"
                    placeholder="John"
                    value={contactForm.first_name}
                    onChange={(e) => setContactForm({...contactForm, first_name: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">Last Name</label>
                  <Input
                    data-testid="contact-last-name"
                    placeholder="Doe"
                    value={contactForm.last_name}
                    onChange={(e) => setContactForm({...contactForm, last_name: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">Email *</label>
                  <Input
                    data-testid="contact-email"
                    type="email"
                    placeholder="john@company.com"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">Company</label>
                  <Input
                    data-testid="contact-company"
                    placeholder="Acme Inc."
                    value={contactForm.company}
                    onChange={(e) => setContactForm({...contactForm, company: e.target.value})}
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">What brings you here?</label>
                <select
                  data-testid="contact-inquiry-type"
                  value={contactForm.inquiry_type}
                  onChange={(e) => setContactForm({...contactForm, inquiry_type: e.target.value})}
                  className="w-full h-10 px-3 rounded-md border border-zinc-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="general">General Inquiry</option>
                  <option value="investor">Investor / VC Inquiry</option>
                  <option value="partnership">Partnership Opportunity</option>
                  <option value="demo">Request a Demo</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">Message *</label>
                <Textarea
                  data-testid="contact-message"
                  placeholder="Tell us about your project or inquiry..."
                  value={contactForm.message}
                  onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                  className="min-h-[120px]"
                  required
                />
              </div>
              
              <Button
                data-testid="contact-submit-btn"
                type="submit"
                disabled={submitting}
                className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Message
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 md:px-12 lg:px-24 py-12 bg-zinc-900">
        <div className="max-w-7xl mx-auto flex flex-col items-center gap-6">
          <WowCXLogo size="md" />
          <div className="flex items-center gap-6">
            <a href="mailto:sales@wowcx.io" className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
              <Mail className="w-4 h-4" />
              <span>sales@wowcx.io</span>
            </a>
            <a href="https://wa.me/+919704217524" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              <span>WhatsApp</span>
            </a>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <a 
              href="https://wowcx.io/terms-of-use" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-zinc-400 hover:text-white transition-colors"
            >
              Terms of Use
            </a>
            <span className="text-zinc-600">|</span>
            <a 
              href="https://wowcx.io/privacy-policy" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-zinc-400 hover:text-white transition-colors"
            >
              Privacy Policy
            </a>
          </div>
          <p className="text-zinc-500 text-sm">&copy; {new Date().getFullYear()} Wow!CX. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;